import React from "react";

// ─── Watermark — versione vettoriale (iter 312 r12)
// L'utente ha richiesto il watermark con casetta-P + "prontoimmo.it"
// in alta risoluzione. Usiamo SVG inline così resta crisp a qualsiasi
// scala (preview, PDF 424 DPI, video Reel). Niente più PNG raster
// che si pixelava su zoom alti.
//
// La forma è una casetta semplice (tetto a punta + base) con la
// lettera P stilizzata interna. Sotto: wordmark "prontoimmo.it".
export const Watermark = ({ dark = true }) => {
  const stroke = dark ? "#FAFAF7" : "#1C1C1A";
  return (
    <div
      className="absolute bottom-1.5 right-2 z-30 flex flex-col items-center gap-[1px] opacity-55 pointer-events-none"
      style={{ filter: dark ? "drop-shadow(0 1px 2px rgba(0,0,0,0.45))" : "none" }}
      data-testid="watermark-pronto"
    >
      <svg viewBox="0 0 48 48" width="14" height="14" fill="none" aria-hidden="true">
        {/* House outline · tetto a punta + base */}
        <path
          d="M 4 22 L 24 6 L 44 22 L 44 42 L 4 42 Z"
          stroke={stroke}
          strokeWidth="2.4"
          strokeLinejoin="round"
          fill="none"
        />
        {/* "P" stilizzata dentro la casa */}
        <path
          d="M 17 32 L 17 18 L 25 18 Q 31 18 31 23 Q 31 28 25 28 L 17 28"
          stroke={stroke}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      <span
        className="font-bold"
        style={{
          color: stroke,
          fontSize: "5.5px",
          letterSpacing: "0.04em",
          lineHeight: 1,
          fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
        }}
      >
        prontoimmo.it
      </span>
    </div>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────
// Restituisce TRUE se il layout vuole foto piena/cinematografica.
export const isPhotoFull = (lay) => lay.name === "Elegante" || lay.name === "Moderno";

// iter v28 r434 — User M1346: helper che aggiunge `?bw=1&w=2048&color=...`
// se il logo è servito dal nostro endpoint `/api/public/agency/{uid}/logo`.
// Il backend pre-elabora il PNG via Pillow LANCZOS → logo crisp per stampa
// 300 DPI senza più dover ricolorare client-side via canvas (html2canvas
// non rispettava i filter CSS in modo affidabile + canvas tainted su CORS).
// Per URL esterne (https://...) ritorna l'URL originale invariato.
function _logoBwUrl(url, color = "black") {
  if (!url) return null;
  // Solo se serviamo noi (path relativo o nostro origin)
  if (url.includes("/api/public/agency/") && url.includes("/logo")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}bw=1&w=2048&color=${color}`;
  }
  return url;
}

// ─── Logo agenzia adattato al layout ───────────────────────────────
// Se data.agencyLogoUrl è presente → mostra l'immagine (max 80px wide).
// Altrimenti fallback lockup tipografico per layout.
export const Logo = ({ data, lay, dark = true }) => {
  const color = dark ? "#FAFAF7" : "#1C1C1A";
  const shadow = dark ? "0 2px 8px rgba(0,0,0,0.3)" : "none";

  // iter v28 r434 — User M1346: usa la versione server-side B/W ad alta
  // risoluzione (vedi `_logoBwUrl` + endpoint backend). Niente più filter
  // CSS o canvas recolor client-side: il PNG arriva già pre-elaborato.
  const bwUrl = dark
    ? (data.agencyLogoBwWhiteUrl || data.agencyLogoUrl)
    : (data.agencyLogoBwBlackUrl || data.agencyLogoUrl);

  // iter v28r10 — User: il sito internet NON deve apparire accanto al logo
  // (né nell'immagine logo né nel fallback tipografico). Resta nella scheda
  // contatti sul retro, dove l'agente lo vede solo se l'ha compilato.

  // IMAGE LOGO — se l'agente ha caricato un PNG/SVG nel profilo, ha priorità.
  if (data.agencyLogoUrl) {
    return (
      <div className="text-right leading-none flex flex-col items-end" style={{ color, textShadow: shadow }}>
        <img
          src={bwUrl}
          alt={data.agencyName}
          crossOrigin="anonymous"
          decoding="sync"
          loading="eager"
          style={{
            maxWidth: 110,
            maxHeight: 44,
            objectFit: "contain",
            display: "block",
            filter: dark
              ? "drop-shadow(0 1px 2px rgba(0,0,0,0.45))"
              : "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
            imageRendering: "-webkit-optimize-contrast",
          }}
        />
      </div>
    );
  }

  // TYPOGRAPHIC FALLBACK per layout.
  if (lay.name === "Moderno") {
    return (
      <div className="text-right leading-none" style={{ color, textShadow: shadow }}>
        <div style={{ fontFamily: lay.headingFont, fontWeight: 900, fontSize: 13, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
          {data.agencyName}
        </div>
      </div>
    );
  }
  if (lay.name === "Minimal") {
    return (
      <div className="text-right leading-tight" style={{ color, textShadow: shadow }}>
        <div style={{ fontFamily: lay.bodyFont, fontWeight: 700, fontSize: 9, letterSpacing: "0.06em" }}>
          {data.agencyName}
        </div>
      </div>
    );
  }
  // Elegante / Luxury — serif italico
  return (
    <div className="text-right leading-none" style={{ color, textShadow: shadow }}>
      <div style={{ fontFamily: lay.accentFont, fontStyle: "italic", fontSize: 14 }}>
        {data.agencyName}
      </div>
    </div>
  );
};

// ─── BrandFrame · layout uniforme branding ────────────────────────
// iter 313 r21 v16 — User: "tutti i materiali per occasione devono avere
// in alto a sinistra il logo agenzia, in basso a sinistra nome+numero
// agente, in basso a destra il watermark Pronto".
//
// Sostituisce `<Logo>` (top-right) + `<Watermark>` (bottom-right) + footer
// custom `agencyName` con uno schema unico e coerente su tutti i template.
//
// `theme="dark"` → testo bianco con shadow (default · foto full-bleed)
// `theme="light"` → testo nero (per template con bg chiaro/whitespace)
// `showWatermark` → render del Pronto badge (false su piani pro/premium)
export const BrandFrame = ({ data, theme = "dark", showWatermark = true, lay = null }) => {
  const isDark = theme === "dark";
  const color = isDark ? "#FAFAF7" : "#1C1C1A";
  const shadow = isDark ? "0 1px 3px rgba(0,0,0,0.45)" : "none";
  const bodyFont = lay?.bodyFont || "ui-sans-serif, system-ui, sans-serif";
  const accentFont = lay?.accentFont || lay?.bodyFont || "ui-serif, Georgia, serif";
  // iter v28 r434 — usa il logo pre-elaborato server-side B/W (vedi Logo()).
  const bwUrl = isDark
    ? (data.agencyLogoBwWhiteUrl || data.agencyLogoUrl)
    : (data.agencyLogoBwBlackUrl || data.agencyLogoUrl);

  // iter v28 r441 — User M1354: «il logo non si vede in alto a sinistra
  // anche se ce l'ho. Se non c'è il logo, mostra il nome agenzia, e se
  // non c'è il nome agenzia, mostra il nome utente. Tutte e tre queste
  // opzioni come fallback chain».
  const [logoBroken, setLogoBroken] = React.useState(false);
  // Resetta lo stato logoBroken se cambia l'URL (es. cache buster nuovo)
  React.useEffect(() => { setLogoBroken(false); }, [bwUrl]);
  const showLogo = data.agencyLogoUrl && !logoBroken;
  const topLabel = data.agencyName || data.agentName;  // agency_name → agent_name

  return (
    <>
      {/* TOP-LEFT — Fallback chain: Logo → Agency name → Agent name */}
      <div className="absolute top-3 left-3 z-30 pointer-events-none" data-testid="brand-frame-logo">
        {showLogo ? (
          <img
            src={bwUrl}
            alt={data.agencyName || data.agentName || "Agenzia"}
            crossOrigin="anonymous"
            decoding="sync"
            loading="eager"
            // iter v28 r441 — retry chain robusto (vedi NuovoAnnuncio in
            // SocialPosts.jsx r439). Solo dopo 3 fallimenti cade su testo.
            onError={(e) => {
              const el = e.currentTarget;
              const tries = parseInt(el.dataset.retryCount || "0", 10);
              if (tries === 0 && data.agencyLogoUrl && el.src !== data.agencyLogoUrl) {
                el.dataset.retryCount = "1";
                el.src = data.agencyLogoUrl;
              } else if (tries < 2) {
                el.dataset.retryCount = "2";
                const sep = el.src.includes("?") ? "&" : "?";
                el.src = `${el.src}${sep}_r=${Date.now()}`;
              } else {
                setLogoBroken(true);
              }
            }}
            style={{
              maxWidth: 96,
              maxHeight: 38,
              objectFit: "contain",
              display: "block",
              filter: isDark
                ? "drop-shadow(0 1px 2px rgba(0,0,0,0.4))"
                : "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
              imageRendering: "-webkit-optimize-contrast",
            }}
          />
        ) : topLabel ? (
          <div
            style={{
              color,
              textShadow: shadow,
              fontFamily: accentFont,
              fontStyle: "italic",
              fontSize: 13,
              lineHeight: 1.1,
              maxWidth: 180,
            }}
          >
            {topLabel}
          </div>
        ) : null}
      </div>

      {/* BOTTOM-LEFT — Nome agente + numero */}
      {(data.agentName || data.agentPhone) && (
        <div className="absolute bottom-3 left-3 z-30 pointer-events-none" data-testid="brand-frame-agent">
          {data.agentName && (
            <div
              style={{
                color,
                textShadow: shadow,
                fontFamily: bodyFont,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.04em",
                lineHeight: 1.1,
              }}
            >
              {data.agentName}
            </div>
          )}
          {data.agentPhone && (
            <div
              style={{
                color,
                textShadow: shadow,
                fontFamily: bodyFont,
                fontSize: 9,
                fontWeight: 600,
                opacity: 0.85,
                marginTop: 2,
                letterSpacing: "0.02em",
              }}
            >
              {data.agentPhone}
            </div>
          )}
        </div>
      )}

      {/* BOTTOM-RIGHT — Watermark Pronto */}
      {showWatermark && <Watermark dark={isDark} />}
    </>
  );
};

// ─── Data builder ──────────────────────────────────────────────────
// Trasforma `property + agency` (forme raw da API) nel dict `data`
// usato uniformemente da TUTTI i template. Fallback ovunque a esempi
// realistici per evitare layout rotti quando un campo è vuoto.
//
// Slot foto utilizzati dalla brochure (4 pagine):
//   • hero          → Cover + Gallery hero grande
//   • p2,p3,p4      → Descrizione: strip laterale 3 foto orizzontali
//   • p5,p6,p7      → Galleria: 3 thumbnails (DIVERSE dalla descrizione
//                     quando ci sono ≥7 foto → brochure più ricca)
// Se le foto non bastano, la Galleria ripiega su p2,p3,p4.
//
// `property.photoTotalCount` (opzionale) sovrascrive il conteggio
// "+ N foto sulla pagina annuncio" mostrato nella Galleria, utile quando
// il consumer cappa le foto a un sottoinsieme ma vuole mostrare il totale
// originale.
// iter 287 — non duplicare hero come fallback per p2/p3/p4: meglio uno
// slot vuoto bello (vedi shared.jsx fallback /landing/*-after.jpg che fa
// uno still life arredato) che la stessa foto ripetuta 3-4 volte.
// Se l'immobile ha N foto reali, mostriamo SOLO N foto, mai duplicate.
// iter 313 r21 v6 — RIMOSSI fallback fake (€890.000, Milano, surface 100, rooms 3,
// title "Trilocale ristrutturato"): se l'agente non ha compilato un campo
// NON inventiamo dati. Eccezione: foto hero usa landing-after come placeholder
// visivo (il template ha bisogno di QUALCOSA da mostrare anche senza foto).
// `hide_price`/`monthly_rent`/`listing_type` ora sono rispettati come nel
// backend `_compose_post_square` per evitare incoerenze (Post mostra
// "Trattativa riservata" e Story mostra "€ 890.000" simultaneamente).
function _formatPriceLine(property) {
  if (property?.hide_price) return "Trattativa riservata";
  const listingType = String(property?.listing_type || "vendita").toLowerCase();
  if (listingType === "affitto" || listingType === "locazione") {
    const rent = property?.monthly_rent || property?.rent_eur;
    if (rent && rent > 0) return `€ ${Number(rent).toLocaleString("it-IT")}/mese`;
    return "Trattativa riservata";
  }
  if (property?.price && property.price > 0) {
    return `€ ${Number(property.price).toLocaleString("it-IT")}`;
  }
  return "Trattativa riservata";
}

export function buildData(property, agency) {
  const photos = property?.photos || [];
  const heroPhoto = photos[0]?.url || "/landing/rinnova-after.jpg";
  const photo2 = photos[1]?.url || null;
  const photo3 = photos[2]?.url || null;
  const photo4 = photos[3]?.url || null;
  const photo5 = photos[4]?.url || null;
  const photo6 = photos[5]?.url || null;
  const photo7 = photos[6]?.url || null;
  // City fallback: usa il campo `city` SE valorizzato, altrimenti prova a
  // estrarlo dall'indirizzo ("Via X, 12345 Milano (MI)" → "Milano").
  let cityResolved = (property?.city || "").trim();
  if (!cityResolved && property?.address) {
    const m = String(property.address).match(/\b\d{4,5}\s+([A-Za-zÀ-ÿ' -]+?)(?:\s*\(|,|$)/);
    if (m && m[1]) cityResolved = m[1].trim();
  }
  const surfaceVal = property?.surface_area || property?.size_sqm || property?.surface || null;
  const roomsVal = property?.rooms || null;
  return {
    title: property?.title || "",
    subtitle: property?.subtitle || property?.headline || "",
    description: property?.description || property?.ai_description || null,
    property_type: property?.property_type || property?.type || null,
    floor: property?.floor || null,
    bathrooms: property?.bathrooms || property?.servizi || null,
    energy_class: property?.energy_class || null,
    photoTotalCount: property?.photoTotalCount || photos.length,
    city: cityResolved,
    surface: surfaceVal,
    rooms: roomsVal,
    price: _formatPriceLine(property),
    address: property?.address || "",
    openHouse: {
      day: property?.open_house_day || "Domenica",
      date: property?.open_house_date || "14 giugno",
      time: property?.open_house_time || "Ore 14:00 — 18:00",
    },
    priceShort: (property?.price && property.price > 0)
      ? `€${Math.round(property.price / 1000)}k`
      : (property?.hide_price ? "Trattativa" : ""),
    previousPrice: property?.previous_price
      ? `€ ${property.previous_price.toLocaleString("it-IT")}`
      : "",
    deltaPct: property?.previous_price && property?.price
      ? Math.round((1 - property.price / property.previous_price) * 100)
      : 0,
    daysToSell: property?.days_to_sell || null,
    listingType: property?.listing_type || "Vendita",
    agencyName: agency?.name || "",
    agencyWeb: agency?.web || "",
    agencyAddress: agency?.address || "",
    agentName: agency?.agent_name || "",
    agentPhone: agency?.agent_phone || "",
    agentEmail: agency?.agent_email || "",
    agentLicense: agency?.agent_license || "",
    agentPhoto: agency?.agent_photo || null,
    agencyLogoUrl: agency?.logo_url || null,
    // iter v28 r434 — User M1346: logo agenzia in alta definizione per stampa.
    // Se il logo è servito dal nostro endpoint `/api/public/agency/{uid}/logo`
    // (path relativo o stesso origin), aggiungiamo `?bw=1&w=2048&color=black|white`
    // così il backend pre-elabora il PNG (recolor server-side via Pillow LANCZOS).
    // Il template usa direttamente l'<img src=...> senza più bisogno di filter
    // CSS o ricolorazione canvas client-side (che html2canvas non rispettava).
    agencyLogoBwBlackUrl: _logoBwUrl(agency?.logo_url, "black"),
    agencyLogoBwWhiteUrl: _logoBwUrl(agency?.logo_url, "white"),
    agencyLogoIsMonochrome: !!agency?.logo_is_monochrome,
    areaShort: agency?.area_short || cityResolved || "",
    landingQrUrl: agency?.landing_qr_url || null,
    estimateQrUrl: agency?.estimate_qr_url || null,
    estimateUrlShort: agency?.estimate_url_short || "",
    estimateUrlFull: agency?.estimate_url_full || "",
    photos: { hero: heroPhoto, p2: photo2, p3: photo3, p4: photo4, p5: photo5, p6: photo6, p7: photo7 },
  };
}
